import Analytics from '@/utils/analytics';
import i18n from '@/utils/i18n';
import React, { useMemo, useState } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import {
  CARD_VIEW_V2_GRID_CELL_LIST_ITEM_STYLE,
  CARD_VIEW_V2_GRID_LIST_ITEM_STYLE,
} from '@/utils/card-view-v2-layout';
import { FocusSource, useComponentFocus } from '@/hooks/use-component-focus';
import { ItemListOption } from '@/contexts/item-context-types';
import { Props as ListItemProps } from '@/components/list-item';
import { MinimalOrderableItemListItemV2 } from '@/components/minimal-orderable-item-list-item-v2';
import { Props as NavigationHeaderProps } from '@/components/navigation-header';
import { OrderableItemList } from '@/components/orders/orderable-item-list';
import { OrderableItemListItemWithRowQuantity } from '@/components/orders/orderable-item-list-item';
import { PageContainer } from '@/components/page-container';
import { StyleSheet, View } from 'react-native';
import { useCardViewV2ProductGridLayout } from '@/hooks/use-card-view-v2-product-grid-layout';
import { useItemDetailContext } from '@/contexts/item-detail-context';
import { useItemRecommendationsContext } from '@/contexts/item-recommendations-context';
import { useNewOrderActionsContext } from '@/contexts/new-order-actions-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useShowListView } from '@/hooks/use-show-list-view';
import { useSmallLayoutSizeValue } from '@/contexts/layout-context';
import { useSupplierDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { ViewOrderButton } from '@/components/partners/view-order-button';

const ITEM_RECOMMENDATIONS_SCREEN_PREFIX = 'item_recommendations_screen';

export const ItemRecommendationsListScreen: React.FC = () => {
  const { itemRecommendations, loading } = useItemRecommendationsContext();
  const { debouncedUpdateQuantity } = useNewOrderActionsContext();
  const { bottom } = useSafeAreaInsets();
  const { showListView, showListViewToggle } = useShowListView({
    enableOnSmallLayouts: true,
  });
  const listRowItemLayout = showListView && showListViewToggle;
  const cardViewV2Enabled = useSupplierDynamicConfigValue(
    'card_view_v2_enabled',
    false,
  );
  const shouldUseCardV2Grid = cardViewV2Enabled && !showListView;
  const cardV2Grid = useCardViewV2ProductGridLayout(shouldUseCardV2Grid);
  const { selectItemListOption } = useItemDetailContext();
  const [seenRecommendations, setSeenRecommendations] = useState<Set<string>>(
    new Set(),
  );

  const baseNavHeaderProps = useSmallLayoutSizeValue<
    Partial<NavigationHeaderProps>
  >(
    {
      showBackButton: true,
    },
    {
      backButtonLabel: i18n.t('back_to', { name: i18n.t('catalog') }),
      showBackButton: false,
    },
  );

  const navigationHeaderProps = useMemo(() => {
    return {
      ...baseNavHeaderProps,
      responsive: true,
      title: i18n.t('based_on_history'),
    };
  }, [baseNavHeaderProps]);

  const listItemProps: Partial<ListItemProps<ItemListOption>> = useMemo(() => {
    return {
      ...(shouldUseCardV2Grid
        ? { listItemStyle: CARD_VIEW_V2_GRID_CELL_LIST_ITEM_STYLE }
        : {}),
      onSelect: (item: ItemListOption) => {
        selectItemListOption(item, {
          itemRecommendationUUID: item.metadata!.itemRecommendationUUID,
          itemUUID: item.metadata!.itemUUID,
          source: ITEM_RECOMMENDATIONS_SCREEN_PREFIX,
        });
      },
      renderMainContent: (item: ItemListOption) => {
        if (shouldUseCardV2Grid) {
          return (
            <MinimalOrderableItemListItemV2
              item={item}
              onSelect={() => {
                selectItemListOption(item, {
                  itemRecommendationUUID: item.metadata!.itemRecommendationUUID,
                  itemUUID: item.metadata!.itemUUID,
                  source: ITEM_RECOMMENDATIONS_SCREEN_PREFIX,
                });
              }}
              size="large"
              style={styles.cardV2ListItem}
              updateQuantity={(updateQuantityData) => {
                debouncedUpdateQuantity(updateQuantityData, {
                  source: ITEM_RECOMMENDATIONS_SCREEN_PREFIX,
                });
              }}
            />
          );
        }

        return (
          <OrderableItemListItemWithRowQuantity
            isFocused={false}
            option={item}
            showListView={listRowItemLayout}
            source={ITEM_RECOMMENDATIONS_SCREEN_PREFIX}
            updateQuantity={(updateQuantityData) => {
              debouncedUpdateQuantity(updateQuantityData, {
                source: ITEM_RECOMMENDATIONS_SCREEN_PREFIX,
              });
            }}
          />
        );
      },
    };
  }, [
    debouncedUpdateQuantity,
    selectItemListOption,
    listRowItemLayout,
    shouldUseCardV2Grid,
  ]);

  useComponentFocus({
    onFocus: (focusSource: FocusSource) => {
      if (focusSource === FocusSource.Navigation) {
        setSeenRecommendations(new Set());
      }
    },
  });

  return (
    <PageContainer
      animateNavigationHeader
      navigationHeaderProps={navigationHeaderProps}
    >
      {(scrollViewProps) => {
        return (
          <>
            {loading ? (
              <View style={styles.activityWrapper}>
                <ActivityIndicator />
              </View>
            ) : (
              <OrderableItemList
                gridColumnCount={
                  shouldUseCardV2Grid ? cardV2Grid.columnCount : undefined
                }
                hasExternalSeparatorControl={shouldUseCardV2Grid}
                listContentContainerStyle={
                  shouldUseCardV2Grid
                    ? cardV2Grid.listContentContainerStyle
                    : undefined
                }
                listData={itemRecommendations.map((ir) => ir?.metadata!.item)}
                listItemProps={listItemProps}
                listRowItemLayout={listRowItemLayout}
                onViewableItemsChanged={({ viewableItems }) => {
                  for (const viewableItem of viewableItems) {
                    const item = viewableItem.item as ItemListOption;
                    const itemRecommendationUUID =
                      item.metadata?.itemRecommendationUUID;
                    if (
                      itemRecommendationUUID &&
                      !seenRecommendations.has(itemRecommendationUUID)
                    ) {
                      seenRecommendations.add(itemRecommendationUUID);
                      Analytics.track(
                        `${ITEM_RECOMMENDATIONS_SCREEN_PREFIX}.recommendation_card.view`,
                        {
                          itemRecommendationUUID,
                          itemUUID: item.metadata!.itemUUID,
                          variantPackUUID: item.metadata!.variantPackUUID,
                        },
                      );
                    }
                  }
                }}
                scrollViewProps={scrollViewProps}
                shouldRenderSeparator={() => !shouldUseCardV2Grid}
                showListView={showListView}
              />
            )}
            <ViewOrderButton style={{ marginBottom: bottom }} />
          </>
        );
      }}
    </PageContainer>
  );
};

const styles = StyleSheet.create({
  activityWrapper: {
    flex: 1,
    justifyContent: 'center',
  },
  cardV2ListItem: {
    ...CARD_VIEW_V2_GRID_LIST_ITEM_STYLE,
  },
});
